<template>
  <v-theme-provider :theme="resolvedTheme" with-background class="pdf-embed-root">
    <div class="pdf-embed">
      <!-- Loading state (embed loading) -->
      <div v-if="embedLoader.isLoading.value" class="pdf-embed__loading">
        <v-progress-circular indeterminate size="64" width="4" color="primary" />
        <span class="pdf-embed__loading-text">{{ t('embed.loading') }}</span>
      </div>

      <!-- The load failed: what it means to a visitor, never the API's sentence -->
      <PlayerRefusal
        v-else-if="embedLoader.failure.value"
        :failure="embedLoader.failure.value"
        :can-retry="!!props.embedId"
        @retry="handleRetry"
      />

      <!-- Playback info loading -->
      <div v-else-if="isLoadingPlaybackInfo" class="pdf-embed__loading">
        <v-progress-circular indeterminate size="48" width="4" color="primary" />
        <span class="pdf-embed__loading-text">{{ t('embed.loadingDocument') }}</span>
      </div>

      <!-- Billing gate: owner out of credits -->
      <PlayerUnavailable v-else-if="isBlocked" />

      <!-- Player -->
      <PDFPlayer
        v-else-if="playerConfig && currentPlaybackInfo"
        :config="playerConfig"
        :playback-info="currentPlaybackInfo"
        :theme="resolvedTheme"
        :primary-color="primaryColor"
        :logo-url="logoUrl"
        :logo-click-url="logoClickUrl"
        @document-load="handleDocumentLoad"
        @page-change="handlePageChange"
        @zoom-change="handleZoomChange"
        @view-mode-change="$emit('viewModeChange', $event)"
        @item-change="handleItemChange"
        @error="$emit('error', $event)"
        @print="$emit('print')"
        @download="$emit('download')"
        @ended="handleEnded"
      />
    </div>
  </v-theme-provider>
</template>

<script setup lang="ts">
import { ref, computed, watch, toRef } from 'vue'
import type { PropType } from 'vue'
import type { Embed } from '../../types/embed'
import type { DocumentAssetListItem, DocumentPlaybackInfo } from '../../types/asset'
import type { PDFPlayerConfig, PDFViewMode, PDFZoom, PDFOutlineItem } from '../types'
import type { PlayerError } from '../../types/base'
import type { CollectionItem } from '../../types/player'
import { useEmbedLoader } from '../../common/composables/useEmbedLoader'
import PlayerRefusal from '../../common/components/PlayerRefusal.vue'
import { usePlaybackInfoCache } from '../../common/composables/usePlaybackInfoCache'
import { isSingleAsset } from '../../common/stillEnd'
import PDFPlayer from './PDFPlayer.vue'
import PlayerUnavailable from '../../common/components/PlayerUnavailable.vue'
import { createUUID } from '../../types/base'
import { usePDFI18n } from '../i18n'

const { t } = usePDFI18n()

const props = defineProps({
  /** Embed ID to load */
  embedId: {
    type: String,
    default: undefined,
  },
  /** Embed data (if already loaded) */
  embed: {
    type: Object as PropType<Embed<DocumentAssetListItem>>,
    default: undefined,
  },
  /** Viewer ID for analytics */
  viewerId: {
    type: String,
    default: undefined,
  },
  /** Initial view mode */
  viewMode: {
    type: String as PropType<PDFViewMode>,
    default: 'continuous',
  },
  /** Initial zoom level or mode */
  zoom: {
    type: [Number, String] as PropType<PDFZoom>,
    default: 'auto',
  },
  /** Initial page number */
  startPage: {
    type: Number,
    default: 1,
  },
  /** Show thumbnail sidebar */
  showThumbnails: {
    type: Boolean,
    default: false,
  },
  /** Show outline sidebar */
  showOutline: {
    type: Boolean,
    default: false,
  },
  /** Enable text search */
  enableSearch: {
    type: Boolean,
    default: true,
  },
  /** Enable text selection */
  enableTextSelection: {
    type: Boolean,
    default: true,
  },
  /** Enable print functionality */
  enablePrint: {
    type: Boolean,
    default: true,
  },
  /** Enable download functionality */
  enableDownload: {
    type: Boolean,
    default: true,
  },
  /** Pages to pre-render ahead */
  renderAhead: {
    type: Number,
    default: 2,
  },
  /** Player theme */
  theme: {
    type: String as PropType<'dark' | 'light'>,
    default: 'dark',
  },
  /** Primary color override (CSS color value, e.g. hex) */
  primaryColor: {
    type: String,
    default: undefined,
  },
  /** Workspace logo URL to overlay on the player */
  logoUrl: {
    type: String,
    default: undefined,
  },
  /** Optional URL the logo links to when clicked */
  logoClickUrl: {
    type: String,
    default: undefined,
  },
})

const emit = defineEmits<{
  embedLoad: [embed: Embed<DocumentAssetListItem>]
  itemChange: [index: number, item: CollectionItem<DocumentAssetListItem>]
  documentLoad: [totalPages: number, outline?: PDFOutlineItem[]]
  pageChange: [page: number]
  zoomChange: [zoom: number, mode: string | null]
  viewModeChange: [mode: string]
  error: [error: PlayerError]
  print: []
  download: []
  /** A document played alone was read to its last page, which is its end (`../../common/stillEnd.ts`). */
  ended: []
}>()

// PDF viewer only supports light/dark; normalize 'colorful' and any other value.
const resolvedTheme = computed<'dark' | 'light'>(() =>
  props.theme === 'light' ? 'light' : 'dark',
)

// Embed loader
const embedIdRef = toRef(props, 'embedId')
const embedPropRef = toRef(props, 'embed')

const embedLoader = useEmbedLoader<DocumentAssetListItem>({
  embedId: embedIdRef,
  embed: embedPropRef,
  autoLoad: true,
})

// Playback info cache
const playbackInfoCache = usePlaybackInfoCache<DocumentPlaybackInfo>({
  playbackToken: computed(() => embedLoader.embed.value?.playbackToken),
  itemToken: computed(() => embedLoader.embed.value?.itemToken),
  collectionPlaybackId: computed(() => embedLoader.embed.value?.collectionPlaybackId),
  sessionId: computed(() => embedLoader.embed.value?.sessionId),
})

// Current item index (PDF typically has only one document per embed)
const currentIndex = ref(0)
const isLoadingPlaybackInfo = ref(false)

// Current playback info
const currentPlaybackInfo = computed<DocumentPlaybackInfo | undefined>(() => {
  const embed = embedLoader.embed.value
  if (!embed || embed.items.length === 0) return undefined

  const currentItem = embed.items[currentIndex.value]
  if (!currentItem) return undefined

  return playbackInfoCache.get(currentItem.asset.uid)
})

// Public billing gate: owner out of credits — show a neutral "unavailable" state.
const isBlocked = computed(() => currentPlaybackInfo.value?.blocked === true)

// Player config - derived from embed
const playerConfig = computed<PDFPlayerConfig | null>(() => {
  const embed = embedLoader.embed.value
  if (!embed) return null

  return {
    uid: embed.uid,
    title: embed.name,
    items: embed.items.map((item, index) => ({
      asset: item.asset,
      position: index,
      customTitle: item.customTitle,
      customDescription: item.customDescription,
    })),
    currentIndex: currentIndex.value,
    viewerId: createUUID(props.viewerId || 'viewer'),
    viewMode: props.viewMode,
    zoom: props.zoom,
    startPage: props.startPage,
    showThumbnails: props.showThumbnails,
    showOutline: props.showOutline,
    enableSearch: props.enableSearch,
    enableTextSelection: props.enableTextSelection,
    enablePrint: props.enablePrint,
    enableDownload: props.enableDownload,
    renderAhead: props.renderAhead,
  }
})

/**
 * Load playback info for an asset
 */
async function loadPlaybackInfo(assetUid: string): Promise<DocumentPlaybackInfo> {
  return playbackInfoCache.fetchOne(assetUid)
}

/**
 * Handle document load event
 */
function handleDocumentLoad(totalPages: number, outline?: PDFOutlineItem[]): void {
  emit('documentLoad', totalPages, outline)
}

/**
 * Handle page change event
 */
function handlePageChange(page: number): void {
  emit('pageChange', page)
}

/**
 * Handle zoom change event
 */
function handleZoomChange(zoom: number, mode: string | null): void {
  emit('zoomChange', zoom, mode)
}

/**
 * Handle collection item change
 */
async function handleItemChange(index: number): Promise<void> {
  const embed = embedLoader.embed.value
  if (!embed || index < 0 || index >= embed.items.length) return

  currentIndex.value = index
  const currentItem = embed.items[index]

  emit('itemChange', index, currentItem)

  // Load playback info for the new item if not cached
  if (!playbackInfoCache.has(currentItem.asset.uid)) {
    isLoadingPlaybackInfo.value = true
    try {
      await loadPlaybackInfo(currentItem.asset.uid)
    } catch {
      // Error will be handled by the player
    } finally {
      isLoadingPlaybackInfo.value = false
    }
  }

  // Prefetch adjacent items
  const adjacentUids: string[] = []
  if (index > 0) adjacentUids.push(embed.items[index - 1].asset.uid)
  if (index < embed.items.length - 1) adjacentUids.push(embed.items[index + 1].asset.uid)

  const uncachedUids = adjacentUids.filter((uid) => !playbackInfoCache.has(uid))
  if (uncachedUids.length > 0) {
    playbackInfoCache.fetchBatch(uncachedUids).catch(() => {})
  }
}

/**
 * A document played alone has ended once its last page is reached — the player says so once per
 * document opened. A collection's documents have no single end and say nothing.
 */
function handleEnded(): void {
  if (isSingleAsset(embedLoader.embed.value)) emit('ended')
}

/**
 * Handle retry button click
 */
function handleRetry(): void {
  if (props.embedId) {
    embedLoader.load(props.embedId)
  }
}

// Watch for embed load and load playback info
watch(
  () => embedLoader.embed.value,
  async (embed) => {
    if (embed) {
      emit('embedLoad', embed)

      // Load first item's playback info
      if (embed.items.length > 0) {
        isLoadingPlaybackInfo.value = true
        try {
          await loadPlaybackInfo(embed.items[0].asset.uid)
        } catch {
          // Error will be handled by the player
        } finally {
          isLoadingPlaybackInfo.value = false
        }
      }
    }
  },
  { immediate: true }
)

// Expose methods for external control
defineExpose({
  embedLoader,
  playbackInfoCache,
  reload: () => props.embedId && embedLoader.load(props.embedId),
  /** Open another document of a collection — what a page's own list of its entries calls. */
  goToIndex: (index: number) => handleItemChange(index),
})
</script>

<style lang="scss" scoped>
.pdf-embed-root {
  width: 100%;
  height: 100%;
  display: flex;
}

.pdf-embed {
  width: 100%;
  height: 100%;
  display: flex;
  flex-direction: column;

  &__loading {
    flex: 1;
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    gap: 12px;
  }

  &__loading-text {
    font-size: 13px;
    color: rgba(var(--v-theme-on-surface), 0.75);
    max-width: 300px;
    text-align: center;
  }
}
</style>
